import { EAnimalTypesUI } from 'data/types/animal.types';

/**
 * Attributive singular: "Your pet application", "Your ESA application". ESA stays capitalised
 * mid-sentence because it is an initialism, not a word.
 */
const ANIMAL_TYPE_NOUNS: Record<EAnimalTypesUI, string> = {
  [EAnimalTypesUI.PET]: 'pet',
  [EAnimalTypesUI.ESA]: 'ESA',
  [EAnimalTypesUI.SA]: 'service animal',
};

/**
 * PP-10057. The one wording used wherever an auto non-managed retraction is explained to a resident.
 *
 * The property name is composed here rather than on the backend because each surface knows its own
 * residency — the onboarding steps from the residency being onboarded, the animals page from the
 * card's own residency — while the backend only holds the property's configured message. Naming the
 * property matters: a resident can hold residencies at several property managements at once, and the
 * same animal type may be managed at the others. The type is named for the same reason: a property
 * can exclude any combination of the three, so "your animal" would leave the resident guessing which
 * of their animals this is about.
 */
export const formatAutoRetractionReason = (
  animalType: EAnimalTypesUI | undefined | null,
  propertyName: string | undefined | null,
  configuredMessage: string | undefined | null,
): string => {
  const property = propertyName || 'this property';
  const type = ANIMAL_TYPE_NOUNS[animalType ?? EAnimalTypesUI.PET];

  const prefix = `Your ${type} application for ${property} has been retracted`;

  if (!configuredMessage) {
    return `${prefix}.`;
  }

  return `${prefix} for the following reason: ${configuredMessage}`;
};

/**
 * The three types are named Pet / ESA / Service animal throughout, in that order — the same rule the
 * PM app follows. Plural, because every sentence that carries them is about the type in general
 * rather than this one animal.
 */
const ANIMAL_TYPE_PLURALS: Record<EAnimalTypesUI, string> = {
  [EAnimalTypesUI.PET]: 'Pets',
  [EAnimalTypesUI.ESA]: 'ESAs',
  [EAnimalTypesUI.SA]: 'Service animals',
};

/**
 * The heading of the not-managed warning, wherever a resident sees it — the residency card, both
 * onboarding steps and the retraction dialog all take it from here.
 *
 * Worded identically to the admin's own preview of this block in the PM app, so the person writing
 * the message and the person reading it are looking at the same sentence. The two apps share no
 * code, so the string is duplicated on purpose: change it in both.
 */
export const formatNotManagedTitle = (
  animalType?: EAnimalTypesUI | null,
): string =>
  `${
    ANIMAL_TYPE_PLURALS[animalType ?? EAnimalTypesUI.PET]
  } are not managed by OurPetPolicy at this property`;
